import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { useIsOwnSpace, useMediaLimits } from "@/api/hooks";
import { useAppSelector } from "@/state/redux/hooks";
import { selectMe } from "@/state/redux/slices/ui";
import { HGAnyRecordData, HGMediaType, HGRecordUrl } from "@/common/types";
import {
  classifyPastedText,
  clipboardMediaFiles,
  isEditableTarget,
  mediaTypeForFile,
} from "@/lib/paste";
import { acceptMediaFiles, PendingMediaFile } from "./MediaInput";
import { normalizeUrl } from "./MultiUrlInput";
import RecordSheet from "./RecordSheet";

interface Draft {
  record: HGAnyRecordData;
  pending: PendingMediaFile[];
  key: number;
}

// Listens for pastes anywhere on the page and opens a prefilled "note" record
// form. Short text → title, long/multi-line text → content, a URL → the URL
// list, images/media → attachments. Pastes inside an open form or a focused
// text field are left alone (handled by RecordForm / native paste).
export default function RecordPasteListener({
  collectionIDs,
}: {
  collectionIDs: string[];
}) {
  const me = useAppSelector(selectMe);
  const isOwnSpace = useIsOwnSpace();
  const limits = useMediaLimits();
  const [draft, setDraft] = useState<Draft | null>(null);
  const keyRef = useRef(0);

  const openDraft = (record: HGAnyRecordData, pending: PendingMediaFile[]) => {
    setDraft({ record, pending, key: keyRef.current++ });
  };

  const handlePaste = (e: ClipboardEvent) => {
    if (!me) return; // creating a record requires auth
    // `collectionIDs` comes from the browsed collection, so on someone else's
    // page the draft would be filed into theirs.
    if (!isOwnSpace) return;
    // A record form is open — let its own onPaste handle it
    if (document.querySelector("[data-record-form]")) return;

    const cd = e.clipboardData;
    if (!cd) return;

    const base: HGAnyRecordData = {
      title: "",
      content: "",
      url: [] as HGRecordUrl[],
      collection_id: collectionIDs,
    };

    const files = clipboardMediaFiles(cd);
    if (files.length > 0) {
      const pending: PendingMediaFile[] = [];
      const byType = new Map<HGMediaType, File[]>();
      for (const file of files) {
        const mt = mediaTypeForFile(file);
        byType.set(mt, [...(byType.get(mt) ?? []), file]);
      }
      for (const [mt, mtFiles] of byType) {
        const alreadyCount = pending.filter((p) => p.mediaType === mt).length;
        const { accepted } = acceptMediaFiles(
          mtFiles,
          mt,
          limits?.[mt],
          alreadyCount,
        );
        pending.push(...accepted);
      }
      if (pending.length === 0) return; // nothing valid to attach
      e.preventDefault();
      openDraft(base, pending);
      return;
    }

    if (isEditableTarget(e.target)) return; // native paste into the field

    const fields = classifyPastedText(cd.getData("text/plain"));
    if (!fields.title && !fields.content && !fields.url) return;
    e.preventDefault();
    openDraft(
      {
        ...base,
        title: fields.title ?? "",
        content: fields.content ?? "",
        url: fields.url ? [{ label: "", url: normalizeUrl(fields.url) }] : [],
      },
      [],
    );
  };

  // Keep the latest handler in a ref so the listener is attached only once.
  // Written in a layout effect, not during render: mutating a ref while
  // rendering isn't safe under concurrent rendering.
  const handlerRef = useRef(handlePaste);
  useLayoutEffect(() => {
    handlerRef.current = handlePaste;
  });

  useEffect(() => {
    const listener = (e: ClipboardEvent) => handlerRef.current(e);
    document.addEventListener("paste", listener);
    return () => document.removeEventListener("paste", listener);
  }, []);

  if (!draft) return null;

  return (
    <RecordSheet
      key={draft.key}
      open
      record={draft.record}
      initialPending={draft.pending}
      title="New record"
      onOpenChange={(o) => {
        if (!o) setDraft(null);
      }}
    />
  );
}
